import React, { Suspense, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { getCookie } from 'cookies-next';
import { isEmpty } from 'lodash';
import {
  ClearRefinements,
  useStats,
  useHits,
  useInstantSearch,
  useSearchBox,
  useCurrentRefinements,
} from 'react-instantsearch';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { TealiumUTag } from 'shared/types/tealium/TealiumUTag';
import PageInfo from '@/components/customercenter-ui/svgs/pageInfo';
import { triggerTealiumEvent, getPageUtagData } from '@/components/customercenter-ui/utils/tealiumUtils';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { createAlgoliaClient } from '@/helpers/algolia/algoliaV5';
import { isPlBadgeConfigurationsEnabled } from '@/helpers/utils/plpProductBadging';
import type { ProductBadgingEntry } from '@/helpers/utils/productBadging';
import { TranslateText, PathNames, SetCookie, getPageUrl, getSelectedDivision } from '@/helpers/utils/siteUtils';
import { CustomFacets, CustomPagination, NumberOfProducts } from './components';
import { useProductListingContext } from './components/context/productListingContext';
import { CustomCurrentRefinements } from './components/customCurrentRefinement';
import { ProductItem } from './components/productItem';
import SearchBarComponent from './components/searchBox';
import { PageTexts } from './i18n';
import { SortBy } from './sortBy';
import { ALOGLIA_SUCCESS_STATUS, COOKIE_KEYS, DEFAULT_SITE_LANG } from '../../../../../constants/constant';
import CustomerCartDetails from '../../customer-detail/components/Cart/CartDetails';
import BackButton from '../../ui/back-button';
import { Button } from '../../ui/button';
import Loader from '../../ui/loader';
import { getContentByTitle } from '../details/helpers/utils/productDataUtils';
const CreditSummary = dynamic(() => import('../../customer-detail/components/CreditSummary/creditSummary'), {
  ssr: false,
  loading: () => null,
});
const swatchCache: Record<string, any[]> = {};
let oldHitsValue: any = null;
let oldQuery: any = '';
const CMS_PRODUCT_BADGING_TITLE = 'Product Badging';
const ProductListingPage = () => {
  const { searchQuery, loading, setLoading } = useProductListingContext();
  const router = useRouter();
  const { status } = useInstantSearch();
  const { query } = useSearchBox();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const params = useSearchParams();
  const [showFacets, setShowFacets] = useState(false);
  const [backUrl, setBackUrl] = useState('');
  const { nbHits } = useStats();
  const { hits } = useHits();
  const pathName = usePathname()?.split('/')?.[1];
  const fromOrderDetails = params.get('fromOrder') ?? false;
  const searchParams = window.location.href;
  const projectSettings: ProjectSettings = useProjSettings();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hitsClone, setHitsClone] = useState<any>();
  const [siteId, setSiteId] = useState<string>();
  const allowedCreditSites = JSON.parse(projectSettings?.allowedCreditSites ?? 'ZBSite');
  const { items } = useCurrentRefinements();

  useEffect(() => {
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    setSiteId(cookieSiteId);
  }, []);

  useEffect(() => {
    if (hits?.length === 1 && nbHits === 1 && status === ALOGLIA_SUCCESS_STATUS) {
      const isBackUrl = params?.get('backUrl') ?? 'false';
      if (isBackUrl === 'false') {
        router.push(`/product/${hits?.[0]?.slug}/${hits?.[0]?.objectID}`);
      }
    }
  }, [status]);

  useEffect(() => {
    if (nbHits > 0) {
      setShowFacets(true);
    } else {
      setShowFacets(false);
    }
  }, [searchQuery, nbHits]);

  useEffect(() => {
    if (fromOrderDetails) {
      const backUrlPath = getPageUrl(PathNames.ORDER_DETAILS) ?? '';
      const url = new URL(`${window.location.origin}${backUrlPath}`);
      url.searchParams.set('fromOrder', 'true');
      setBackUrl(url.toString());
    } else {
      const backurl = getPageUrl(PathNames.CUSTOMER_DETAILS);
      setBackUrl(backurl);
    }
  }, [fromOrderDetails]);

  useEffect(() => {
    const currentParams = new URL(window.location.href).searchParams.toString();
    const currentUrl = `/${pathName}/?${currentParams}`;
    const previousHistory = getCookie(COOKIE_KEYS.URL_HISTORY);
    if (previousHistory) {
      const history = JSON.parse(previousHistory);
      const newHistory = { ...history };
      if (!newHistory[pathName]) {
        newHistory[pathName] = { link: currentUrl, name: '' };
      } else {
        newHistory[pathName].link = currentUrl;
      }
      SetCookie(COOKIE_KEYS.URL_HISTORY, JSON.stringify(newHistory));
    }
    const currentQuery = new URL(window.location.href).searchParams.get('q');
    if (currentQuery === query) {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (hits && hits !== oldHitsValue) {
      oldHitsValue = hits;
      setHitsClone(hits);
    }
  }, [hits]);

  useEffect(() => {
    const currentQuery = new URL(window.location.href).searchParams.get('q');
    if (hits && hits.length > 0) {
      const pageLinkUtagData: TealiumUTag = getPageUtagData(
        'Product Catalog',
        undefined,
        undefined,
        undefined,
        undefined,
        { hits, nbHits, searchQuery, items },
      );
      triggerTealiumEvent(pageLinkUtagData);
    } else if (hits?.length === 0 && nbHits === 0 && oldQuery === currentQuery) {
      const pageLinkUtagData: TealiumUTag = getPageUtagData(
        'No Search Results',
        undefined,
        undefined,
        undefined,
        undefined,
        { hits: [], nbHits: 0, searchQuery: searchQuery, categoryHit: {} },
      );
      triggerTealiumEvent(pageLinkUtagData);
    }
    oldQuery = currentQuery;
  }, [hits]);

  return (
    <section className="mb-14 px-4">
      {loading && <Loader />}
      <section className="mr-4 flex items-center">
        <section className="mr-auto">
          <SearchBarComponent setLoading={setLoading} />
        </section>
        <BackButton
          text={fromOrderDetails ? Texts.backToOrderDetails : Texts.backToCustomerDetails}
          className="flex items-center text-blueLight underline"
          url={backUrl}
        />
      </section>
      <section className="flex items-start gap-4 px-4">
        <section className="w-60 overflow-hidden rounded-md border-[1px] border-foggyGray">
          <section className="flex items-center gap-2 border-b border-foggyGray p-4">
            <PageInfo />
            <strong className="mr-auto text-lg font-bold text-black">{Texts.Filters}</strong>
            <section className="flex items-center">
              {searchQuery ? (
                <Button
                  className="text-center text-sm text-blueLight underline underline-offset-2"
                  onClick={() => router.push('/search')}
                >
                  Clear all
                </Button>
              ) : (
                <ClearRefinements
                  classNames={{
                    disabledButton: 'hidden',
                    button: 'text-sm underline underline-offset-2 text-center text-blueLight',
                  }}
                  translations={{
                    resetButtonText: 'Clear all',
                  }}
                />
              )}
            </section>
          </section>
          {showFacets ? <CustomFacets /> : <></>}
        </section>
        <section className="flex w-4/6 flex-col p-4">
          <section className="mb-2 flex items-baseline">
            <section className={`mb-2 mr-auto flex items-center ${searchQuery ? 'block' : 'hidden'}`}>
              <section className="mr-auto">
                <NumberOfProducts search={query} totalHits={nbHits} />
              </section>
            </section>
            <section className="ml-auto flex items-center justify-end">
              <SortBy />
            </section>
          </section>
          <section className="mb-3">
            <div className="flex w-full flex-wrap items-center justify-center">
              <div className="flex grow items-center border-0 border-gray-200">
                <CustomCurrentRefinements />
              </div>
            </div>
          </section>
          <section>
            <ProductGrid algoliaUIReady />
          </section>
          <section className="mb-4 flex justify-center">
            <CustomPagination />
          </section>
        </section>
        <section className="w-1/5">
          <section className="flex flex-col gap-4">
            <CustomerCartDetails />
            <Suspense
              fallback={
                <div className="flex items-center justify-center">
                  <Loader />
                </div>
              }
            >
              {allowedCreditSites?.creditSites?.includes(siteId) && <CreditSummary />}
            </Suspense>
          </section>
        </section>
      </section>
    </section>
  );
};
const ProductGrid = ({ algoliaUIReady }: any) => {
  const { hits } = useHits<any>();
  const { items } = useCurrentRefinements();
  const [hitsClone, setHitsClone] = useState<any>();
  const projectSettings: ProjectSettings = useProjSettings();
  const searchClient = createAlgoliaClient(projectSettings?.algoliaAppId, projectSettings?.algoliaApiKey);
  const [productBadgingEntries, setProductBadgingEntries] = useState<ProductBadgingEntry[] | null | undefined>(
    undefined,
  );
  const selectedDivision = getSelectedDivision() ?? 'ZB';

  const getContentByTitleRef = useRef(getContentByTitle);
  getContentByTitleRef.current = getContentByTitle;

  const badgeSettings = projectSettings?.badgeConfigurations.find(
    (badge: any) => badge?.['SITE_ID'] === selectedDivision,
  )?.BADGES_LIST?.[0];

  useEffect(() => {
    if (!isPlBadgeConfigurationsEnabled(badgeSettings?.ENABLE_BADGE_CONFIGURATIONS)) {
      setProductBadgingEntries(undefined);
      return;
    }
    setProductBadgingEntries(null);
    let cancelled = false;
    getContentByTitleRef
      .current(CMS_PRODUCT_BADGING_TITLE, true)
      .then((data: any) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length === 0) {
          setProductBadgingEntries([]);
          return;
        }
        const entries = data?.productBadgingEntries ?? data?.data?.productBadgingEntries;
        setProductBadgingEntries(Array.isArray(entries) ? entries : []);
      })
      .catch(() => {
        if (!cancelled) setProductBadgingEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [badgeSettings?.ENABLE_BADGE_CONFIGURATIONS, projectSettings?.siteId]);

  function generateSizeWidthCombinations(filters: any) {
    if (!filters && filters.length === 0) {
      return [];
    }
    let sizes: any = [];
    let widths: any = [];
    const combinations: any = [];

    // Extract sizes and widths from the input array
    filters.forEach((filter: any) => {
      if (filter.attribute === 'refinementSize') {
        sizes = filter.refinements.map((refinement: any) => refinement.label);
      }
      if (filter.attribute === 'footwearWidth') {
        widths = filter.refinements.map((refinement: any) => refinement.label);
      }
    });

    // Create combinations of sizes and widths
    if (sizes.length > 0 && widths.length > 0) {
      sizes.forEach((size: any) => {
        widths.forEach((width: any) => {
          combinations.push(`${trimSpaceFromFilter(size)}-${trimSpaceFromFilter(width)}`);
        });
      });
    }
    if (sizes.length > 0 && widths.length === 0) {
      sizes.forEach((size: any) => {
        combinations.push(`${trimSpaceFromFilter(size)}`);
      });
    }
    if (sizes.length === 0 && widths.length > 0) {
      widths.forEach((width: any) => {
        combinations.push(`${trimSpaceFromFilter(width)}`);
      });
    }

    return combinations;
  }

  function addInventoryFlag(productData: any, combinationArray: any) {
    // If combinationArray is empty, set inventoryPresent to true at all levels
    if (!combinationArray || combinationArray.length === 0) {
      productData.inventoryPresent = true;

      if (productData.swatches && Array.isArray(productData.swatches)) {
        productData.swatches = productData.swatches.map((swatch: any) => {
          swatch.inventoryPresent = true;
          return swatch;
        });
      }

      return productData;
    }

    // Check if any combination is true at the main level
    const mainLevelInventoryPresent = combinationArray.some(
      (combination: any) => productData?.swMap?.[combination] === true,
    );

    // Add the inventoryPresent flag at the main level
    productData.inventoryPresent = mainLevelInventoryPresent;

    // Check if any combination is true at the swatch level and add the flag
    if (productData.swatches && Array.isArray(productData.swatches)) {
      productData.swatches = productData.swatches.map((swatch: any) => {
        const swatchLevelInventoryPresent = combinationArray.some(
          (combination: any) => swatch?.swMap?.[combination] === true,
        );
        swatch.inventoryPresent = swatchLevelInventoryPresent;
        return swatch;
      });
    }

    return productData;
  }

  useEffect(() => {
    const fetchSwatches = async (revisedHits: any[], cookieSiteId: string) => {
      const allSwatchKeys = revisedHits?.flatMap((hit) => hit?.swatchesKey || []).filter(Boolean);
      if (allSwatchKeys.length === 0) {
        return revisedHits;
      }
      const uniqueKeys = Array.from(new Set(allSwatchKeys));
      const cachedMap: Record<string, any[]> = {};
      const missingKeys: string[] = [];
      uniqueKeys?.forEach((key) => {
        if (swatchCache[key]) {
          cachedMap[key] = swatchCache?.[key];
        } else {
          missingKeys.push(key);
        }
      });

      let fetchedMap: Record<string, any[]> = {};
      if (missingKeys?.length > 0) {
        const { hits: swatchHits } = await searchClient.searchSingleIndex({
          indexName: `swatches_${cookieSiteId}`,
          searchParams: {
            query: '',
            filters: missingKeys.map((k) => `key:"${k}"`).join(' OR '),
            hitsPerPage: missingKeys.length,
          },
        });

        fetchedMap = swatchHits?.reduce((map: Record<string, any[]>, swatch: any) => {
          (map[swatch.key] ||= []).push(swatch);
          swatchCache[swatch.key] = map?.[swatch.key];
          return map;
        }, {});
      }
      const swatchMap = { ...cachedMap, ...fetchedMap };
      return revisedHits?.map((hit) => {
        if (hit?.swatchesKey?.length) {
          hit.swatches = hit?.swatchesKey?.flatMap((key: string) => swatchMap?.[key] || []);
          delete hit.swatchesKey;
        }
        return hit;
      });
    };
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    if (hits && hits !== oldHitsValue && cookieSiteId) {
      const sizeWidthCombinations = generateSizeWidthCombinations(items);
      fetchSwatches(hits, cookieSiteId).then((enrichedHits) => {
        const revisedHits = enrichedHits.map((hit: any) => addInventoryFlag(hit, sizeWidthCombinations));
        setHitsClone(revisedHits);
        oldHitsValue = revisedHits;
      });
    }
  }, [hits]);

  return (
    <>
      <section className="plpGrid">
        <ul>
          {algoliaUIReady &&
            hitsClone?.map((hit: any, index: number) => {
              const ratings = Number(hit?.averageRating) ?? '1';
              return (
                <li key={`${hit?.objectID}-${index}`}>
                  <ProductItem
                    hit={hit}
                    rating={ratings}
                    productBadgingEntries={productBadgingEntries}
                    filters={items}
                  />
                </li>
              );
            })}
        </ul>
      </section>
    </>
  );
};

const trimSpaceFromFilter = (filter: any) => {
  if (isEmpty(filter)) {
    return '';
  }
  return filter
    .trim()
    .replace(/\s{2,}/g, ' ')
    .split(' ')
    .join('-');
};

export default ProductListingPage;
